import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  calculateDocumentTotals, 
  calculateItemPricing, 
  validateItemData, 
  formatCurrency 
} from '../utils/calculationUtils';
import TruckTypeSelect from './TruckTypeSelect';
import OpportunitySelect from './OpportunitySelect';
import OperationSelect from './OperationSelect';
import PaymentTermsSelector from './PaymentTermsSelector';

const DocumentFormWithPricing = ({ 
  document = null, 
  onSubmit, 
  onCancel, 
  opportuniteId = null,
  operationId = null 
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    type: 'devis',
    template_style: 'advanced',  // Par défaut template avancé
    opportunite_id: opportuniteId || document?.opportunite_id || '',
    operation_id: operationId || document?.operation_id || '',
    devis_id: document?.devis_id || '',  // Pour les bons de commande liés à un devis
    parent_document_id: document?.parent_document_id || '',  // Document parent
    appliquer_tva_globale: true,  // Par défaut TVA applicable (transport local CI)
    appliquer_retenue_forfaitaire: false,  // Contrôle indépendant de la retenue forfaitaire 2%
    client: {
      name: '',
      address: '',
      rccm: '',
      contact: '',
      email: '',
      phone: ''
    },
    objet: '',
    conditions: '',
    delai_retractation: '10',
    inclus: '• Transport des matériels de construction depuis Abidjan à rendu diverses destinations sur remorque\n• Prise en charge jusqu\'au point de livraison\n• Débarquer déchargement 24H\n• Franchise frontières défit par frontière',
    exclus: '• TVA 18% applicable en sus\n• Hors passage frontière, frais de transit\n• Les charges liées à une marchandise dangereuse\n• Hors assurance\n• Franchise immobilisation camion après chargement\n• Autres frais non listés mais induits par l\'opération',
    notes: '• Les frais d\'enlèvement et de livraison doivent être accessibles au transporteur\n• La prise en charge est matérialisée par la signature conjointe du document\n• Le planning des opérations doit être transmis 48H avant',
    modalites_paiement: `📌 Modalité : Paiement par acompte (Standard Baobab Connect : 70% / 30%)

Modalité recommandée pour la majorité des opérations.

• 70% avant départ
  (avance carburant + mobilisation du camion)

• 30% à la livraison
  (débloqués après réception des preuves de livraison et validation commerciale)

Aucun chauffeur n'est assigné tant que le premier versement n'est pas confirmé.`,
    emetteur: user?.title ? `${user.full_name} - ${user.title}` : (user?.full_name || 'Émetteur par défaut'),
    suivi_par: user?.title ? `${user.full_name} - ${user.title}` : (user?.full_name || 'Suivi par défaut'),
    items: [
      {
        ref: '',
        description: '',
        quantity: 1,
        unit: 'remorque',
        poids: '-',
        unite_physique: 'remorque',
        unite_payante: 'unité',  // Maintenant inclut les types de camions
        tarif_transporteur_ht: 0,
        marge_pourcentage: 20,  // Défaut: 20% marge HT totale des courses
        marge_ht: 0,
        unit_price: 0,
        total_ht: 0,
        tva_rate: 18,
        appliquer_tva: true,
        total_tva: 0,
        total_ttc: 0,
        comment: ''
      }
    ],
    total_transporteur_ht: 0,
    total_marge_ht: 0,
    total_ht: 0,
    total_tva: 0,
    total_ttc: 0,
    montant_paye: 0,
    reste_a_payer: 0,
    // Phase 1 - Module Facturation: Frais d'immobilisation
    frais_immobilisation_camion: 0,
    nb_jours_immobilisation: 0,
    montant_jour_immobilisation: 100000, // Défaut: 100,000 FCFA par jour
    part_transporteur_immobilisation: 0,
    
    // Phase 2: Nouveau système de statuts
    document_status: null,  // Statut spécifique au type de document
    payment_status: null    // Statut de paiement
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [operations, setOperations] = useState([]);
  const [devis, setDevis] = useState([]);
  const [devisSearchTerm, setDevisSearchTerm] = useState('');
  const [showAllDevis, setShowAllDevis] = useState(false);
  const [factures, setFactures] = useState([]);
  const [factureSearchTerm, setFactureSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [truckTypes, setTruckTypes] = useState([]);
  
  // États spécifiques aux bons de commande
  const [transporteurs, setTransporteurs] = useState([]);
  const [selectedTransporteur, setSelectedTransporteur] = useState(null);
  const [bonCommandeInfo, setBonCommandeInfo] = useState({
    nom_fournisseur: '',
    telephone_fournisseur: '',
    cni_fournisseur: '',
    projet: '',
    date_execution: '',
    instructions_speciales: 'Les véhicules désignés doivent être en bon état de fonctionnement\nRespecter les délais de livraison convenus\nSignaler toute modification 48h à l\'avance',
    vehicles: [
      { tracteur: '', semi_remorque: '' }
    ]
  });
  
  // États pour la gestion de la flotte
  const [showFleetSelector, setShowFleetSelector] = useState(false);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [selectedFleetVehicles, setSelectedFleetVehicles] = useState([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  
  // États pour les templates CGT
  const [cgtTemplates, setCgtTemplates] = useState([]);
  const [selectedCGTTemplate, setSelectedCGTTemplate] = useState('');
  
  // États pour le nouveau système de statuts (Phase 2)
  const [availableDocumentStatuses, setAvailableDocumentStatuses] = useState([]);
  const [availablePaymentStatuses, setAvailablePaymentStatuses] = useState([]);

  // Fonction pour filtrer les opérations par opportunité (pour l'affichage)
  const getFilteredOperations = () => {
    return operations; // Les opérations sont déjà filtrées par l'API
  };

  // Fonction pour charger les opérations filtrées par opportunité
  const loadFilteredOperations = async (opportuniteId = null) => {
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };

    let url = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
    if (opportuniteId) {
      url += `?opportunite_id=${opportuniteId}`;
    }

    try {
      const opResponse = await fetch(url, { headers });
      if (opResponse.ok) {
        const opData = await opResponse.json();
        setOperations(opData);
      }
    } catch (error) {
      console.error('Error loading operations:', error);
    }
  };

  // Charger les données des listes déroulantes
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Charger les opportunités
        const oppResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities`, {
          headers
        });
        if (oppResponse.ok) {
          const oppData = await oppResponse.json();
          setOpportunities(oppData);
        }

        // Charger les opérations (toutes initialement)
        await loadFilteredOperations();

        // Charger les clients
        const clientsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/clients`, {
          headers
        });
        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json();
          setClients(clientsData);
        }

        // Charger les types de camions
        const truckTypesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/truck-types`, {
          headers
        });
        if (truckTypesResponse.ok) {
          const truckTypesData = await truckTypesResponse.json();
          setTruckTypes(truckTypesData.truck_types || []);
        }

        // Charger les transporteurs (pour les bons de commande)
        const transporteursResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transporteurs`, {
          headers
        });
        if (transporteursResponse.ok) {
          const transporteursData = await transporteursResponse.json();
          setTransporteurs(transporteursData);
        }

        // Charger les templates CGT
        const cgtTemplatesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates`, {
          headers
        });
        if (cgtTemplatesResponse.ok) {
          const cgtTemplatesData = await cgtTemplatesResponse.json();
          setCgtTemplates(cgtTemplatesData);
        }

        // Charger les devis existants pour les liaisons factures
        const devisResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents`, {
          headers
        });
        if (devisResponse.ok) {
          const allDocuments = await devisResponse.json();
          const devisData = allDocuments.filter(doc => doc.type === 'devis');
          const facturesData = allDocuments.filter(doc => doc.type === 'facture');
          setDevis(devisData);
          setFactures(facturesData);
        }

      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    loadData();
  }, []);

  // Charger les statuts disponibles pour le type de document actuel
  useEffect(() => {
    const loadStatuses = async () => {
      if (!formData.type) return;
      
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/statuses/${formData.type}`,
          { headers }
        );
        
        if (response.ok) {
          const data = await response.json();
          setAvailableDocumentStatuses(data.document_statuses || []);
          setAvailablePaymentStatuses(data.payment_statuses || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statuts:', error);
      }
    };

    loadStatuses();
  }, [formData.type]);

  // Réinitialiser les champs spécifiques selon le type de document
  const handleDocumentTypeChange = (newType) => {
    setFormData({ ...formData, type: newType });
    
    if (newType === 'bon_commande') {
      // Adapter les valeurs par défaut pour les bons de commande
      setBonCommandeInfo({
        ...bonCommandeInfo,
        date_execution: new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0] // +7 jours
      });
    }
  };

  // Fonctions pour la gestion des transporteurs dans les bons de commande
  const handleTransporteurSelect = (transporteurId) => {
    const transporteur = transporteurs.find(t => t.id === transporteurId);
    if (transporteur) {
      setSelectedTransporteur(transporteur);
      setBonCommandeInfo({
        ...bonCommandeInfo,
        nom_fournisseur: transporteur.name || transporteur.nom || '',
        telephone_fournisseur: transporteur.phone || transporteur.telephone || '',
        cni_fournisseur: transporteur.cni || ''
      });
    } else {
      setSelectedTransporteur(null);
      setBonCommandeInfo({
        ...bonCommandeInfo,
        nom_fournisseur: '',
        telephone_fournisseur: '',
        cni_fournisseur: ''
      });
    }
  };

  const addVehicleToBonCommande = () => {
    setBonCommandeInfo({
      ...bonCommandeInfo,
      vehicles: [...bonCommandeInfo.vehicles, { tracteur: '', semi_remorque: '' }]
    });
  };

  const removeVehicleFromBonCommande = (index) => {
    const newVehicles = bonCommandeInfo.vehicles.filter((_, i) => i !== index);
    setBonCommandeInfo({
      ...bonCommandeInfo,
      vehicles: newVehicles.length > 0 ? newVehicles : [{ tracteur: '', semi_remorque: '' }]
    });
  };

  const updateVehicleInBonCommande = (index, field, value) => {
    const newVehicles = [...bonCommandeInfo.vehicles];
    newVehicles[index] = { ...newVehicles[index], [field]: value };
    setBonCommandeInfo({
      ...bonCommandeInfo,
      vehicles: newVehicles
    });
  };

  // Fonctions pour la gestion de la flotte
  const loadTransporteurFleet = async () => {
    if (!selectedTransporteur?.id) {
      alert('Veuillez d\'abord sélectionner un transporteur');
      return;
    }
    
    setLoadingFleet(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fleet-vehicles?transporteur_id=${selectedTransporteur.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const vehicles = data?.vehicles || [];
        
        if (vehicles.length === 0) {
          alert('Aucun véhicule trouvé dans la flotte de ce transporteur');
          return;
        }
        
        setFleetVehicles(vehicles);
        setShowFleetSelector(true);
        alert(`${vehicles.length} véhicule(s) disponible(s) dans la flotte. Sélectionnez ceux que vous souhaitez utiliser.`);
      } else {
        throw new Error('Erreur lors du chargement de la flotte');
      }
    } catch (error) {
      console.error('Erreur chargement flotte:', error);
      alert('Erreur lors du chargement des véhicules de la flotte');
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleFleetVehicleSelection = (vehicleId, isSelected) => {
    if (isSelected) {
      setSelectedFleetVehicles([...selectedFleetVehicles, vehicleId]);
    } else {
      setSelectedFleetVehicles(selectedFleetVehicles.filter(id => id !== vehicleId));
    }
  };

  // Ajouter les véhicules sélectionnés depuis la flotte
  const addSelectedFleetVehicles = () => {
    const vehiclesToAdd = fleetVehicles
      .filter(vehicle => selectedFleetVehicles.includes(vehicle.id))
      .map(vehicle => ({
        tracteur: vehicle.immatriculation_1 || '',
        semi_remorque: vehicle.immatriculation_2 || '',
        type_camion: vehicle.type_camion || '',
        capacite: vehicle.capacite_max_tonnes || '',
        fromFleet: true
      }));

    setBonCommandeInfo({
      ...bonCommandeInfo,
      vehicles: [...bonCommandeInfo.vehicles, ...vehiclesToAdd]
    });

    // Réinitialiser la sélection
    setSelectedFleetVehicles([]);
    setShowFleetSelector(false);
    
    alert(`${vehiclesToAdd.length} véhicule(s) ajouté(s) depuis la flotte`);
  };

  // Gérer les changements de paiement pour les factures
  const handlePaymentChange = (montantPaye) => {
    const resteAPayer = formData.total_ttc - montantPaye;
    setFormData({
      ...formData,
      montant_paye: montantPaye,
      reste_a_payer: Math.max(0, resteAPayer)
    });
  };

  // Gérer la sélection d'un devis pour les bons de commande
  const handleDevisSelect = async (devisId) => {
    if (!devisId) {
      // Réinitialiser si pas de devis sélectionné
      setFormData({
        ...formData,
        devis_id: '',
        parent_document_id: '',
        opportunite_id: '',
        operation_id: '',
        client: {
          name: '',
          address: '',
          rccm: '',
          contact: '',
          email: '',
          phone: ''
        }
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${devisId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const devisData = await response.json();
        
        // Pré-remplir les données depuis le devis sélectionné
        setFormData({
          ...formData,
          devis_id: devisId,
          parent_document_id: devisId,
          opportunite_id: devisData.opportunite_id || '',
          operation_id: devisData.operation_id || '',
          client: devisData.client || formData.client,
          objet: devisData.objet ? `Bon de commande - ${devisData.objet}` : formData.objet,
          items: devisData.items || formData.items,
          total_transporteur_ht: devisData.total_transporteur_ht || 0,
          total_marge_ht: devisData.total_marge_ht || 0,
          total_ht: devisData.total_ht || 0,
          total_tva: devisData.total_tva || 0,
          total_ttc: devisData.total_ttc || 0,
          appliquer_tva_globale: devisData.appliquer_tva_globale !== false
        });

        // Charger les opérations liées à l'opportunité si elle existe
        if (devisData.opportunite_id) {
          await loadFilteredOperations(devisData.opportunite_id);
        }

        console.log('Devis linked successfully:', devisData.reference);
      } else {
        console.error('Error loading devis details');
        alert('Erreur lors du chargement des détails du devis');
      }
    } catch (error) {
      console.error('Error linking devis:', error);
      alert('Erreur lors de la liaison avec le devis');
    }
  };

  // Gérer la sélection d'un devis pour les factures (OBLIGATOIRE)
  const handleDevisSelectForFacture = async (devisId) => {
    if (!devisId) {
      // Réinitialiser si pas de devis sélectionné
      setFormData({
        ...formData,
        devis_id: '',
        opportunite_id: '',
        operation_id: '',
        client: {
          name: '',
          address: '',
          rccm: '',
          contact: '',
          email: '',
          phone: ''
        }
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${devisId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const devisData = await response.json();
        
        // Pré-remplir les données depuis le devis sélectionné pour la facture
        setFormData({
          ...formData,
          devis_id: devisId,
          opportunite_id: devisData.opportunite_id || '',
          operation_id: devisData.operation_id || '',
          client: devisData.client || formData.client,
          objet: devisData.objet ? `Facturation - ${devisData.objet}` : formData.objet,
          items: devisData.items || formData.items,
          total_transporteur_ht: devisData.total_transporteur_ht || 0,
          total_marge_ht: devisData.total_marge_ht || 0,
          total_ht: devisData.total_ht || 0,
          total_tva: devisData.total_tva || 0,
          total_ttc: devisData.total_ttc || 0,
          appliquer_tva_globale: devisData.appliquer_tva_globale !== false
        });

        // Charger les opérations liées à l'opportunité si elle existe
        if (devisData.opportunite_id) {
          await loadFilteredOperations(devisData.opportunite_id);
        }

        console.log('Devis linked to facture successfully:', devisData.reference);
      } else {
        console.error('Error loading devis details for facture');
        alert('Erreur lors du chargement des détails du devis');
      }
    } catch (error) {
      console.error('Error linking devis to facture:', error);
      alert('Erreur lors de la liaison avec le devis');
    }
  };

  // Fonction pour appliquer un template CGT
  const applyCGTTemplate = async () => {
    if (!selectedCGTTemplate) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Préparer les données du formulaire pour le traitement du template
      const templateData = {
        items: formData.items,
        operation_data: {
          lieu_collecte: formData.lieu_collecte || '',
          lieu_livraison: formData.lieu_livraison || ''
        },
        modalites_paiement: formData.modalites_paiement || '30 jours, dépôt de facture'
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates/${selectedCGTTemplate}/process`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(templateData)
        }
      );

      if (response.ok) {
        const processedTemplate = await response.json();
        
        // Mettre à jour les champs avec le template traité
        setFormData(prev => ({
          ...prev,
          inclus: processedTemplate.inclus,
          exclus: processedTemplate.exclus,
          notes: processedTemplate.notes
        }));

        // Afficher un message de confirmation
        const templateName = cgtTemplates.find(t => t.id === selectedCGTTemplate)?.name;
        alert(`Template "${templateName}" appliqué avec succès !`);
        
        // Réinitialiser la sélection
        setSelectedCGTTemplate('');
      } else {
        throw new Error('Erreur lors du traitement du template');
      }
    } catch (error) {
      console.error('Erreur lors de l\'application du template CGT:', error);
      alert('Erreur lors de l\'application du template CGT. Veuillez réessayer.');
    }
  };

  // Fonction pour filtrer les devis selon les critères de recherche et statut
  const getFilteredDevis = () => {
    let filtered = devis;
    
    // Filtrer par statut si nécessaire
    if (!showAllDevis) {
      filtered = filtered.filter(d => 
        d.statut === 'accepté' || 
        d.statut === 'validé' || 
        d.statut === 'acceptée' || 
        d.statut === 'validée' ||
        d.statut === 'brouillon' ||
        d.statut === 'envoyé'
      );
    }
    
    // Filtrer par terme de recherche
    if (devisSearchTerm.trim()) {
      const searchLower = devisSearchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.reference?.toLowerCase().includes(searchLower) ||
        d.id?.toLowerCase().includes(searchLower) ||
        d.client?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered.sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
  };

  // Fonction pour filtrer les factures selon les critères de recherche
  const getFilteredFactures = () => {
    let filtered = factures;
    
    // Filtrer par terme de recherche
    if (factureSearchTerm.trim()) {
      const searchLower = factureSearchTerm.toLowerCase();
      filtered = filtered.filter(f => 
        f.reference?.toLowerCase().includes(searchLower) ||
        f.id?.toLowerCase().includes(searchLower) ||
        f.client?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered.sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
  };

  // Fonction pour rendre la section client
  const renderClientSection = () => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">Informations Client</h3>
      
      {/* Sélection client depuis la base */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Sélectionner un client existant
        </label>
        <select
          value={selectedClient?.id || ''}
          onChange={(e) => handleClientSelect(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Sélectionner un client</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.name} {client.rccm ? `(${client.rccm})` : ''}
            </option>
          ))}
        </select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du client *
          </label>
          <input
            type="text"
            value={formData.client.name}
            onChange={(e) => setFormData({
              ...formData,
              client: { ...formData.client, name: e.target.value }
            })}
            className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.clientName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Nom de l'entreprise"
            required
          />
          {errors.clientName && <p className="text-red-500 text-sm mt-1">{errors.clientName}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            RCCM
          </label>
          <input
            type="text"
            value={formData.client.rccm}
            onChange={(e) => setFormData({
              ...formData,
              client: { ...formData.client, rccm: e.target.value }
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Numéro RCCM"
          />
        </div>
      </div>
      
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Adresse *
        </label>
        <textarea
          value={formData.client.address}
          onChange={(e) => setFormData({
            ...formData,
            client: { ...formData.client, address: e.target.value }
          })}
          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.clientAddress ? 'border-red-500' : 'border-gray-300'
          }`}
          rows={3}
          placeholder="Adresse complète du client"
        />
        {errors.clientAddress && <p className="text-red-500 text-sm mt-1">{errors.clientAddress}</p>}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact
          </label>
          <input
            type="text"
            value={formData.client.contact}
            onChange={(e) => setFormData({
              ...formData,
              client: { ...formData.client, contact: e.target.value }
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nom du contact"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.client.email}
            onChange={(e) => setFormData({
              ...formData,
              client: { ...formData.client, email: e.target.value }
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="email@exemple.com"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone
          </label>
          <input
            type="tel"
            value={formData.client.phone}
            onChange={(e) => setFormData({
              ...formData,
              client: { ...formData.client, phone: e.target.value }
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="+225 XX XX XX XX"
          />
        </div>
      </div>
    </div>
  );

  // Fonction pour rendre la section des items
  const renderItemsSection = () => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Items et Tarification</h3>
        <button
          type="button"
          onClick={addItem}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors"
          style={{
            backgroundColor: formData.type === 'devis' ? '#16a34a' : 
                            formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
          }}
        >
          Ajouter un item
        </button>
      </div>
      
      {formData.items.map((item, index) => (
        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 mb-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-medium text-gray-900">Item {index + 1}</h4>
            {formData.items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Supprimer
              </button>
            )}
          </div>
          
          {/* Informations principales de l'item */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-3">Informations de base</h5>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Désignation (Départ → Transit → Arrivée) *
              </label>
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors[`item_${index}_description`] ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Abidjan → Yamoussoukro → Bouaké"
              />
              {errors[`item_${index}_description`] && <p className="text-red-500 text-sm mt-1">{errors[`item_${index}_description`]}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantité *
                </label>
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`item_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  min="0"
                  step="1"
                />
                {errors[`item_${index}_quantity`] && <p className="text-red-500 text-sm mt-1">{errors[`item_${index}_quantity`]}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unité payante *
                </label>
                <TruckTypeSelect
                  value={item.unite_payante}
                  onChange={(value) => updateItem(index, 'unite_payante', value)}
                  placeholder="Rechercher un type de camion ou une unité..."
                  required
                  includeStandardUnits={true}
                />
              </div>
            </div>
          </div>

          {/* Tarification */}
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-semibold text-blue-700">💰 Tarification</h5>
              
              {/* Toggle pour le mode de calcul */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Mode manuel</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.calcul_automatique !== false}
                    onChange={(e) => updateItem(index, 'calcul_automatique', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className="text-sm text-gray-600">Calcul automatique</span>
              </div>
            </div>

            {/* MODE AUTOMATIQUE - Ancien système */}
            {item.calcul_automatique !== false && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarif Transporteur HT *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={item.tarif_transporteur_ht}
                      onChange={(e) => updateItem(index, 'tarif_transporteur_ht', e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors[`item_${index}_tarif`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      min="0"
                      step="any"
                      placeholder="0"
                    />
                    <div className="absolute right-3 top-2 text-gray-500 text-sm">CFA</div>
                  </div>
                  {errors[`item_${index}_tarif`] && <p className="text-red-500 text-sm mt-1">{errors[`item_${index}_tarif`]}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marge (%) - sur coût <span className="text-xs text-gray-500">Défaut: 20%</span>
                    <span className="text-xs text-blue-600 ml-2" title="Formule: (Prix - Coût) / Coût × 100">ℹ️ Markup</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={item.marge_pourcentage}
                      onChange={(e) => updateItem(index, 'marge_pourcentage', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <div className="absolute right-3 top-2 text-gray-500 text-sm">%</div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix Client HT <span className="text-xs text-gray-500">(Calculé auto)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={item.prix_client_ht || item.unit_price}
                      readOnly
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 bg-gray-50 text-gray-700"
                    />
                    <div className="absolute right-3 top-2 text-gray-500 text-sm">CFA</div>
                  </div>
                </div>
              </div>
            )}

            {/* MODE MANUEL - Nouveau système */}
            {item.calcul_automatique === false && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix Client HT * <span className="text-xs text-blue-600">(Ce que vous facturez)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={item.prix_client_ht}
                        onChange={(e) => updateItem(index, 'prix_client_ht', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`item_${index}_prix_client`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        min="0"
                        step="1000"
                        placeholder="0"
                        required
                      />
                      <div className="absolute right-3 top-2 text-gray-500 text-sm">CFA</div>
                    </div>
                    {errors[`item_${index}_prix_client`] && <p className="text-red-500 text-sm mt-1">{errors[`item_${index}_prix_client`]}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tarif Transporteur HT * <span className="text-xs text-orange-600">(Ce que vous reversez)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={item.tarif_transporteur_ht}
                        onChange={(e) => updateItem(index, 'tarif_transporteur_ht', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`item_${index}_tarif`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        min="0"
                        step="any"
                        placeholder="0"
                      />
                      <div className="absolute right-3 top-2 text-gray-500 text-sm">CFA</div>
                    </div>
                    {errors[`item_${index}_tarif`] && <p className="text-red-500 text-sm mt-1">{errors[`item_${index}_tarif`]}</p>}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marge FCFA <span className="text-xs text-gray-500">(Calculée auto)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={item.marge_ht}
                        readOnly
                        className="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 bg-green-50 text-green-700 font-medium"
                      />
                      <div className="absolute right-3 top-2 text-gray-500 text-sm">CFA</div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marge % - sur coût <span className="text-xs text-gray-500">(Calculée auto)</span>
                      <span className="text-xs text-blue-600 ml-2" title="Calculée automatiquement selon la formule markup">ℹ️</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={item.marge_pourcentage_calculee}
                        readOnly
                        className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 bg-green-50 text-green-700 font-medium"
                      />
                      <div className="absolute right-3 top-2 text-gray-500 text-sm">%</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total HT
                </label>
                <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-right font-medium">
                  {item.total_ht.toLocaleString()} CFA
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TVA ({item.tva_rate}%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={item.appliquer_tva !== false}
                    onChange={(e) => updateItem(index, 'appliquer_tva', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">{item.total_tva.toLocaleString()} CFA</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total TTC
                </label>
                <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-blue-50 text-right font-bold text-blue-700">
                  {item.total_ttc.toLocaleString()} CFA
                </div>
              </div>
            </div>
          </div>

          {/* Commentaire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commentaire
            </label>
            <input
              type="text"
              value={item.comment}
              onChange={(e) => updateItem(index, 'comment', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Commentaire ou informations supplémentaires"
            />
          </div>
        </div>
      ))}
      
      {errors.items && <p className="text-red-500 text-sm">{errors.items}</p>}
    </div>
  );

  // Fonction pour rendre la section des totaux
  const renderTotalsSection = () => (
    <div className="p-4 rounded-lg border-2" style={{
      backgroundColor: formData.type === 'devis' ? '#f0fdf4' : 
                      formData.type === 'bon_commande' ? '#fff7ed' : '#eff6ff',
      borderColor: formData.type === 'devis' ? '#16a34a' : 
                   formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
    }}>
      <h3 className="text-lg font-semibold mb-4" style={{
        color: formData.type === 'devis' ? '#16a34a' : 
               formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
      }}>
        Totaux du Document
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Transporteur HT:</span>
            <span className="font-medium">{formatCurrency(formData.total_transporteur_ht)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Marge HT:</span>
            <span className="font-medium">{formatCurrency(formData.total_marge_ht)}</span>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Client HT:</span>
            <span className="font-medium">{formatCurrency(formData.total_ht)}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Appliquer TVA:</span>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.appliquer_tva_globale !== false}
                onChange={(e) => setFormData(prev => ({...prev, appliquer_tva_globale: e.target.checked}))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-500">
                {formData.appliquer_tva_globale !== false ? '(Transport local CI)' : '(Export/International)'}
              </span>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Retenue forfaitaire 2%:</span>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.appliquer_retenue_forfaitaire === true}
                onChange={(e) => setFormData(prev => ({...prev, appliquer_retenue_forfaitaire: e.target.checked}))}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="text-sm text-gray-500">
                {formData.appliquer_retenue_forfaitaire ? '(Majoré +2.04%)' : '(Non appliquée)'}
              </span>
            </div>
          </div>
          
          {/* Section frais d'immobilisation */}
          {formData.type === 'facture' && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">🚛 Frais d'Immobilisation Camion</h4>
              
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-xs text-gray-600">Nombre de jours</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.nb_jours_immobilisation}
                    onChange={(e) => {
                      const jours = parseInt(e.target.value) || 0;
                      setFormData(prev => ({
                        ...prev, 
                        nb_jours_immobilisation: jours,
                        frais_immobilisation_camion: jours * (prev.montant_jour_immobilisation || 100000)
                      }));
                    }}
                    className="w-full px-2 py-1 text-xs border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-600">Montant/jour (FCFA)</label>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.montant_jour_immobilisation}
                    onChange={(e) => {
                      const montant = parseInt(e.target.value) || 0;
                      setFormData(prev => ({
                        ...prev, 
                        montant_jour_immobilisation: montant,
                        frais_immobilisation_camion: (prev.nb_jours_immobilisation || 0) * montant
                      }));
                    }}
                    className="w-full px-2 py-1 text-xs border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-2">
                <label className="text-xs text-gray-600">Part transporteur (FCFA) - Interne</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.part_transporteur_immobilisation}
                  onChange={(e) => setFormData(prev => ({...prev, part_transporteur_immobilisation: parseInt(e.target.value) || 0}))}
                  className="w-full px-2 py-1 text-xs border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Montant reversé au transporteur (invisible client)"
                />
              </div>
              
              {formData.frais_immobilisation_camion > 0 && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  💡 Frais automatique: {formatCurrency(formData.frais_immobilisation_camion)}
                  {formData.part_transporteur_immobilisation > 0 && (
                    <span className="block">Reversé transporteur: {formatCurrency(formData.part_transporteur_immobilisation)}</span>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-between">
            <span className="text-gray-600">Total TVA:</span>
            <span className="font-medium">
              {formData.appliquer_tva_globale !== false ? formatCurrency(formData.total_tva) : '0 FCFA'}
            </span>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-700 font-medium">
              {formData.appliquer_tva_globale !== false ? 'Total TTC:' : 'Total HT (Sans TVA):'}
            </span>
            <span className="font-bold text-lg" style={{
              color: formData.type === 'devis' ? '#16a34a' : 
                     formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
            }}>
              {formData.appliquer_tva_globale !== false ? 
                formatCurrency(formData.total_ttc) : 
                formatCurrency(formData.total_ht)
              }
            </span>
          </div>
          {formData.type === 'facture' && (
            <div className="flex justify-between">
              <span className="text-gray-600">Reste à payer:</span>
              <span className="font-medium">{formData.reste_a_payer.toLocaleString()} CFA</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Fonction pour rendre les autres informations
  const renderAutresInformations = () => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">Autres Informations</h3>
      
      {/* Sélection de template CGT */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <label className="block text-sm font-medium text-blue-700 mb-2">
          📄 Template Conditions Générales de Transport (CGT)
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedCGTTemplate || ''}
            onChange={(e) => setSelectedCGTTemplate(e.target.value)}
            className="flex-1 border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Choisir un template CGT...</option>
            {cgtTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} - {template.description}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyCGTTemplate}
            disabled={!selectedCGTTemplate}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Appliquer
          </button>
        </div>
        <p className="text-xs text-blue-600 mt-1">
          Les templates CGT remplissent automatiquement les sections Inclus, Exclus et Notes avec les conditions appropriées pour votre type de transport.
        </p>
      </div>

      {/* Modalités de paiement */}
      <PaymentTermsSelector
        value={formData.modalites_paiement || ''}
        onChange={(value) => setFormData({ ...formData, modalites_paiement: value })}
        className="mb-4"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inclus
          </label>
          <textarea
            value={formData.inclus}
            onChange={(e) => setFormData({ ...formData, inclus: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Services inclus"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Exclus
          </label>
          <textarea
            value={formData.exclus}
            onChange={(e) => setFormData({ ...formData, exclus: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Services exclus"
          />
        </div>
      </div>
      
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Notes additionnelles"
        />
      </div>
    </div>
  );

  // Fonction pour rendre la section des véhicules (pour les bons de commande)
  const renderVehiclesSection = () => (
    <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
      <h3 className="text-lg font-semibold text-orange-600 mb-4">🚛 Véhicules à affecter</h3>
      
      <div className="bg-white p-4 rounded-lg border">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-800">Véhicules</h4>
          <div className="space-x-2">
            {selectedTransporteur && (
              <button
                type="button"
                onClick={() => setShowFleetSelector(!showFleetSelector)}
                disabled={loadingFleet}
                className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                📋 {showFleetSelector ? 'Masquer' : 'Sélectionner depuis la flotte'}
              </button>
            )}
            <button
              type="button"
              onClick={() => addVehicleToBonCommande()}
              className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              ➕ Ajouter véhicule manuel
            </button>
          </div>
        </div>

        {/* Sélecteur de véhicules de flotte */}
        {showFleetSelector && selectedTransporteur && (
          <div className="mb-4 p-3 bg-blue-50 border rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium text-blue-800">Sélectionner véhicules de la flotte</h5>
              <button
                type="button"
                onClick={() => setShowFleetSelector(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                ✕
              </button>
            </div>
            
            {!fleetVehicles.length ? (
              <div className="text-center py-2">
                <button
                  type="button"
                  onClick={loadTransporteurFleet}
                  disabled={loadingFleet}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loadingFleet ? '⏳ Chargement...' : '📋 Charger flotte'}
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-3 text-sm text-blue-600">
                  Sélectionnez les véhicules à utiliser ({fleetVehicles.length} disponibles) :
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {fleetVehicles.map((vehicle) => (
                    <label key={vehicle.id} className="flex items-start p-2 bg-white rounded border hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedFleetVehicles.includes(vehicle.id)}
                        onChange={(e) => handleFleetVehicleSelection(vehicle.id, e.target.checked)}
                        className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          Tracteur: {vehicle.immatriculation_1 || 'Non spécifié'}
                          {vehicle.immatriculation_2 && (
                            <span className="ml-2">| Remorque: {vehicle.immatriculation_2}</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {vehicle.type_camion && `Type: ${vehicle.type_camion}`}
                          {vehicle.capacite_max_tonnes && ` | Capacité: ${vehicle.capacite_max_tonnes}T`}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="mt-3 flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {selectedFleetVehicles.length} véhicule(s) sélectionné(s)
                  </span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={() => setSelectedFleetVehicles([])}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Tout désélectionner
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedFleetVehicles(fleetVehicles.map(v => v.id))}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      Tout sélectionner
                    </button>
                    <button
                      type="button"
                      onClick={addSelectedFleetVehicles}
                      disabled={selectedFleetVehicles.length === 0}
                      className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ➕ Ajouter sélectionnés
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {bonCommandeInfo.vehicles.map((vehicle, index) => (
          <div key={index} className="mb-3 p-3 bg-gray-50 rounded border">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium text-gray-700">
                Véhicule {index + 1}
                {vehicle.fromFleet && <span className="ml-2 text-xs text-green-600">(Depuis la flotte)</span>}
              </h5>
              {bonCommandeInfo.vehicles.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeVehicleFromBonCommande(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  🗑️ Supprimer
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tracteur
                </label>
                <input
                  type="text"
                  value={vehicle.tracteur}
                  onChange={(e) => updateVehicleInBonCommande(index, 'tracteur', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: 3711EL01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Semi-remorque
                </label>
                <input
                  type="text"
                  value={vehicle.semi_remorque}
                  onChange={(e) => updateVehicleInBonCommande(index, 'semi_remorque', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: 8708AY01"
                />
              </div>
            </div>
            {vehicle.type_camion && (
              <div className="mt-2 text-xs text-gray-500">
                Type: {vehicle.type_camion} {vehicle.capacite && `| Capacité: ${vehicle.capacite}T`}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Rendu conditionnel selon le type de document
  const renderDocumentForm = () => {
    switch (formData.type) {
      case 'devis':
        return renderDevisForm();
      case 'bon_commande':
        return renderBonCommandeForm();
      case 'facture':
        return renderFactureForm();
      default:
        return renderDevisForm();
    }
  };

  // Formulaire spécifique aux DEVIS
  const renderDevisForm = () => (
    <>
      {/* Liaisons */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-green-600 mb-4">Liaisons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opportunité liée
            </label>
            <OpportunitySelect
              value={formData.opportunite_id}
              onChange={async (newOpportuniteId) => {
                setFormData({ 
                  ...formData, 
                  opportunite_id: newOpportuniteId,
                  operation_id: '' // Reset operation when opportunity changes
                });
                // Charger les opérations filtrées par cette opportunité
                await loadFilteredOperations(newOpportuniteId);
              }}
              opportunities={opportunities}
              placeholder="Rechercher une opportunité (numéro, client, nom)..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opération liée
            </label>
            <OperationSelect
              value={formData.operation_id}
              onChange={(operationId) => setFormData({ ...formData, operation_id: operationId })}
              operations={getFilteredOperations()}
              placeholder="Rechercher une opération (numéro, statut)..."
            />
          </div>
        </div>
      </div>

      {/* Informations client */}
      {renderClientSection()}

      {/* Validité du devis */}
      <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
        <h3 className="text-lg font-semibold text-green-600 mb-4">📅 Validité du devis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Validité (en jours) *
            </label>
            <input
              type="number"
              value={formData.delai_retractation}
              onChange={(e) => setFormData({ ...formData, delai_retractation: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="30"
              required
            />
            {errors.validite && <p className="text-red-500 text-sm mt-1">{errors.validite}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objet du devis *
            </label>
            <input
              type="text"
              value={formData.objet}
              onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Transport de marchandises..."
            />
          </div>
        </div>
      </div>

      {/* Services et tarification */}
      {renderItemsSection()}
      {renderTotalsSection()}
      {renderAutresInformations()}
    </>
  );

  // Formulaire spécifique aux BONS DE COMMANDE
  const renderBonCommandeForm = () => (
    <>
      {/* Liaison avec un devis existant */}
      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
        <h3 className="text-lg font-semibold text-blue-600 mb-4">🔗 Liaison avec un devis</h3>
        <p className="text-sm text-blue-700 mb-3">
          Sélectionnez un devis existant pour lier ce bon de commande et récupérer automatiquement l'opportunité et l'opération concernées.
        </p>
        
        <div className="grid grid-cols-1 gap-4 mb-4">
          {/* Barre de recherche pour les devis */}
          <div>
            <label className="block text-sm font-medium text-blue-700 mb-1">
              Rechercher un devis (référence, ID, ou nom client)
            </label>
            <input
              type="text"
              value={devisSearchTerm}
              onChange={(e) => setDevisSearchTerm(e.target.value)}
              placeholder="Tapez pour rechercher un devis..."
              className="w-full border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Option pour afficher tous les devis */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showAllDevis"
              checked={showAllDevis}
              onChange={(e) => setShowAllDevis(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="showAllDevis" className="text-sm text-blue-700">
              Afficher tous les devis (y compris refusés et annulés)
            </label>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-blue-700 mb-1">
              Devis de référence
            </label>
            <select
              value={formData.devis_id || ''}
              onChange={(e) => handleDevisSelect(e.target.value)}
              className="w-full border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              size="5"
            >
              <option value="">-- Sélectionner un devis --</option>
              {getFilteredDevis().map(devis => (
                <option key={devis.id} value={devis.id}>
                  {devis.reference} - {devis.client?.name || 'Client non défini'} 
                  ({formatCurrency(devis.total_ttc || 0)}) - {devis.statut}
                </option>
              ))}
            </select>
            {getFilteredDevis().length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                Aucun devis trouvé avec ces critères de recherche
              </p>
            )}
          </div>
          
          {formData.devis_id && (
            <div className="text-sm text-blue-700 bg-blue-100 p-3 rounded-md">
              <strong>Informations récupérées :</strong>
              <br />• Opportunité : {formData.opportunite_id ? `ID ${formData.opportunite_id}` : 'Non liée'}
              <br />• Opération : {formData.operation_id ? `ID ${formData.operation_id}` : 'Non liée'}
              <br />• Client : {formData.client?.name || 'Non défini'}
            </div>
          )}
        </div>
      </div>

      {/* Informations Transporteur */}
      <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
        <h3 className="text-lg font-semibold text-orange-600 mb-4">🚚 Informations Transporteur</h3>
        
        {/* Sélection transporteur existant */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sélectionner un transporteur existant (optionnel)
          </label>
          <select
            value={selectedTransporteur?.id || ''}
            onChange={(e) => handleTransporteurSelect(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">-- Nouveau transporteur ou saisie manuelle --</option>
            {transporteurs.map(transporteur => (
              <option key={transporteur.id} value={transporteur.id}>
                {transporteur.name || transporteur.nom} {transporteur.phone ? `- ${transporteur.phone}` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {/* Informations transporteur */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du transporteur *
            </label>
            <input
              type="text"
              value={bonCommandeInfo.nom_fournisseur}
              onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, nom_fournisseur: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Nom du transporteur"
              required
            />
            {errors.transporteurName && <p className="text-red-500 text-sm mt-1">{errors.transporteurName}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone *
            </label>
            <input
              type="tel"
              value={bonCommandeInfo.telephone_fournisseur}
              onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, telephone_fournisseur: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Téléphone du transporteur"
              required
            />
            {errors.transporteurPhone && <p className="text-red-500 text-sm mt-1">{errors.transporteurPhone}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CNI du transporteur
            </label>
            <input
              type="text"
              value={bonCommandeInfo.cni_fournisseur}
              onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, cni_fournisseur: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Numéro CNI"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projet
            </label>
            <input
              type="text"
              value={bonCommandeInfo.projet}
              onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, projet: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Nom du projet"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date d'exécution prévue *
          </label>
          <input
            type="date"
            value={bonCommandeInfo.date_execution}
            onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, date_execution: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
          {errors.dateExecution && <p className="text-red-500 text-sm mt-1">{errors.dateExecution}</p>}
        </div>
      </div>

      {/* Informations client */}
      {renderClientSection()}

      {/* Objet du bon de commande */}
      <div className="bg-orange-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-orange-600 mb-4">📋 Objet de la mission</h3>
        <textarea
          value={formData.objet}
          onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          rows={3}
          placeholder="Transport de marchandises selon les spécifications détaillées, depuis Abidjan vers les destinations mentionnées..."
          required
        />
      </div>

      {/* Véhicules à affecter */}
      {renderVehiclesSection()}

      {/* Services à exécuter */}
      {renderItemsSection()}
      {renderTotalsSection()}

      {/* Instructions spéciales */}
      <div className="bg-orange-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-orange-600 mb-4">Instructions spéciales</h3>
        <textarea
          value={bonCommandeInfo.instructions_speciales}
          onChange={(e) => setBonCommandeInfo({...bonCommandeInfo, instructions_speciales: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          rows={4}
          placeholder="Instructions particulières pour le transporteur..."
        />
      </div>
    </>
  );

  // Formulaire spécifique aux FACTURES
  const renderFactureForm = () => (
    <>
      {/* Informations de facturation */}
      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
        <h3 className="text-lg font-semibold text-blue-600 mb-4">🧾 Informations de facturation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numéro de facture
            </label>
            <input
              type="text"
              value={formData.reference || ''}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="F-2024-001"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date d'échéance
            </label>
            <input
              type="date"
              value={formData.date_echeance || ''}
              onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Référence devis/bon de commande
            </label>
            <input
              type="text"
              value={formData.reference_source || ''}
              onChange={(e) => setFormData({ ...formData, reference_source: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Référence du document source"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conditions de paiement
            </label>
            <select
              value={formData.conditions_paiement || '30'}
              onChange={(e) => setFormData({ ...formData, conditions_paiement: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="0">Paiement comptant</option>
              <option value="15">15 jours</option>
              <option value="30">30 jours</option>
              <option value="45">45 jours</option>
              <option value="60">60 jours</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liaison facture vers opportunité, opération et devis */}
      {formData.type && formData.type.trim() === 'facture' && (
        <>
          {/* Section principale - Liaison OBLIGATOIRE avec devis */}
          <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500 mb-6">
            <h3 className="text-lg font-semibold text-red-600 mb-4">🔗 Liaison avec devis (OBLIGATOIRE)</h3>
            <p className="text-sm text-red-700 mb-3">
              <strong>Obligatoire :</strong> Chaque facture doit être liée à un devis pour hériter automatiquement de l'opération et de l'opportunité.
            </p>
            
            <div className="grid grid-cols-1 gap-4 mb-4">
              {/* Barre de recherche pour les devis */}
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">
                  <span className="text-red-500">*</span> Rechercher le devis de base (référence, ID, ou nom client)
                </label>
                <input
                  type="text"
                  value={devisSearchTerm}
                  onChange={(e) => setDevisSearchTerm(e.target.value)}
                  placeholder="Tapez pour rechercher le devis à facturer..."
                  className="w-full border border-red-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Option pour afficher tous les devis */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showAllDevisFacture"
                  checked={showAllDevis}
                  onChange={(e) => setShowAllDevis(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="showAllDevisFacture" className="text-sm text-red-700">
                  Afficher tous les devis (y compris brouillons et refusés)
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">
                  <span className="text-red-500">*</span> Devis à facturer
                </label>
                <select
                  value={formData.devis_id || ''}
                  onChange={(e) => handleDevisSelectForFacture(e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 bg-white ${
                    !formData.devis_id ? 'border-red-500 bg-red-50' : 'border-red-300'
                  }`}
                  size="5"
                  required
                >
                  <option value="">-- SÉLECTIONNER LE DEVIS À FACTURER --</option>
                  {getFilteredDevis().map(devis => (
                    <option key={devis.id} value={devis.id}>
                      {devis.reference} - {devis.client?.name || 'Client non défini'} 
                      ({formatCurrency(devis.total_ttc || 0)}) - {devis.statut}
                    </option>
                  ))}
                </select>
                {getFilteredDevis().length === 0 && (
                  <p className="text-sm text-red-600 mt-1 font-medium">
                    Aucun devis trouvé avec ces critères de recherche
                  </p>
                )}
                {!formData.devis_id && (
                  <p className="text-sm text-red-600 mt-1 font-medium">
                    La sélection d'un devis est obligatoire pour créer une facture
                  </p>
                )}
              </div>
              
              {formData.devis_id && (
                <div className="text-sm text-green-700 bg-green-100 p-3 rounded-md border border-green-300">
                  <strong>✅ Informations héritées du devis :</strong>
                  <br />• <strong>Opportunité :</strong> {formData.opportunite_id ? `ID ${formData.opportunite_id}` : '❌ Non liée au devis'}
                  <br />• <strong>Opération :</strong> {formData.operation_id ? `ID ${formData.operation_id}` : '❌ Non liée au devis'}
                  <br />• <strong>Client :</strong> {formData.client?.name || '❌ Non défini'}
                  <br />• <strong>Montant à facturer :</strong> {formatCurrency(formData.total_ttc || 0)}
                </div>
              )}
            </div>
          </div>
          
          {/* Section secondaire - Liaison facture de référence (OPTIONNELLE pour cas spéciaux) */}
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-4">
            <h4 className="text-md font-semibold text-blue-600 mb-3">📋 Liaison avec facture de référence (Optionnel)</h4>
            <p className="text-xs text-blue-600 mb-2">
              <em>Uniquement pour :</em> Avoirs, factures rectificatives, complémentaires, ou annulations
            </p>
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">
                Facture de référence (pour avoir/rectificative)
              </label>
              {/* Barre de recherche pour les factures */}
              <input
                type="text"
                value={factureSearchTerm}
                onChange={(e) => setFactureSearchTerm(e.target.value)}
                placeholder="Rechercher une facture à corriger/annuler..."
                className="w-full border border-blue-300 rounded-md px-3 py-1 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={formData.facture_reference_id || ''}
                onChange={(e) => setFormData({ ...formData, facture_reference_id: e.target.value })}
                className="w-full border border-blue-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                size="3"
              >
                <option value="">Aucune facture de référence</option>
                {getFilteredFactures().map(f => (
                  <option key={f.id} value={f.id}>
                    {f.reference} - {f.client?.name || 'Client non défini'} - {formatCurrency(f.total_ttc || 0)} - {f.statut}
                  </option>
                ))}
              </select>
              {getFilteredFactures().length === 0 && factureSearchTerm && (
                <p className="text-xs text-gray-500 mt-1">
                  Aucune facture trouvée avec "{factureSearchTerm}"
                </p>
              )}
              <p className="text-xs text-blue-600 mt-2">
                <strong>Cas d'usage :</strong> Avoir (remboursement), rectificative (correction), complémentaire (ajout)
              </p>
            </div>
          </div>
        </>
      )}

      {/* Informations client */}
      {renderClientSection()}

      {/* Objet de la facture */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-600 mb-4">📄 Objet de la facture</h3>
        <textarea
          value={formData.objet}
          onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Facturation pour services de transport réalisés selon bon de commande..."
          required
        />
      </div>

      {/* Services facturés */}
      {renderItemsSection()}

      {/* Paiement */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-600 mb-4">💰 Informations de paiement</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant payé
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.montant_paye}
              onChange={(e) => handlePaymentChange(parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reste à payer
            </label>
            <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
              {formData.reste_a_payer.toLocaleString()} CFA
            </div>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode de paiement
            </label>
            <select
              value={formData.mode_paiement || 'virement'}
              onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="virement">Virement bancaire</option>
              <option value="cheque">Chèque</option>
              <option value="especes">Espèces</option>
              <option value="mobile">Mobile Money</option>
              <option value="carte">Carte bancaire</option>
            </select>
          </div>
        </div>
      </div>

      {renderTotalsSection()}
    </>
  );

  // Initialiser le formulaire avec les données existantes
  useEffect(() => {
    if (document) {
      // Parser delai_retractation pour extraire juste le nombre
      let delaiValue = document.delai_retractation || '10';
      if (typeof delaiValue === 'string' && delaiValue.includes('jour')) {
        delaiValue = delaiValue.replace(/[^0-9]/g, '') || '10';
      }
      
      // Copier TOUTES les données du document
      const documentData = {
        ...document,
        // Client
        client: document.client || formData.client,
        // Items
        items: document.items || formData.items,
        // Validité et délai
        delai_retractation: delaiValue,
        // Liaisons
        opportunite_id: document.opportunite_id || '',
        operation_id: document.operation_id || '',
        devis_id: document.devis_id || '',
        parent_document_id: document.parent_document_id || '',
        // Modalités et conditions
        modalites_paiement: document.modalites_paiement || '',
        conditions_paiement: document.conditions_paiement || '',
        conditions: document.conditions || '',
        // Sections CGT
        inclus: document.inclus || '',
        exclus: document.exclus || '',
        notes: document.notes || '',
        // Objet et référence
        objet: document.objet || '',
        reference: document.reference || '',
        // Paramètres TVA et retenue
        appliquer_tva_globale: document.appliquer_tva_globale !== false,
        appliquer_retenue_forfaitaire: document.appliquer_retenue_forfaitaire === true,
        // Frais d'immobilisation
        frais_immobilisation_camion: document.frais_immobilisation_camion || 0,
        nb_jours_immobilisation: document.nb_jours_immobilisation || 0,
        montant_jour_immobilisation: document.montant_jour_immobilisation || 100000,
        part_transporteur_immobilisation: document.part_transporteur_immobilisation || 0,
        // Emetteur et suivi
        emetteur: document.emetteur || '',
        suivi_par: document.suivi_par || '',
        // Bon de commande
        bon_commande_info: document.bon_commande_info || null,
        // Paiement (pour factures)
        montant_paye: document.montant_paye || 0,
        mode_paiement: document.mode_paiement || '',
        reference_paiement: document.reference_paiement || '',
        date_paiement: document.date_paiement || '',
        statut_paiement: document.statut_paiement || 'non_paye'
      };

      // CORRECTION: Forcer le recalcul des totaux pour corriger les valeurs décimales du backend
      if (documentData.items && documentData.items.length > 0) {
        // Recalculer chaque item avec arrondi
        const calculatedItems = documentData.items.map(item => 
          calculateItemPricing(item, {
            appliquerTvaGlobale: documentData.appliquer_tva_globale !== false,
            appliquerRetenueForfaitaire: documentData.appliquer_retenue_forfaitaire === true
          })
        );
        
        // Recalculer les totaux du document
        const totals = calculateDocumentTotals(calculatedItems, {
          mode: 'advanced',
          appliquerTvaGlobale: documentData.appliquer_tva_globale !== false,
          appliquerRetenueForfaitaire: documentData.appliquer_retenue_forfaitaire === true,
          nbJoursImmobilisation: documentData.nb_jours_immobilisation || 0,
          montantJourImmobilisation: documentData.montant_jour_immobilisation || 0,
          montantPaye: documentData.montant_paye || 0
        });

        setFormData({
          ...documentData,
          items: calculatedItems,
          ...totals
        });
      } else {
        setFormData(documentData);
      }
    }
  }, [document]);

  // Fonction pour calculer automatiquement les prix d'un item
  // FONCTION REMPLACÉE - Utilise maintenant l'utilitaire unifié calculateItemPricing
  const calculateItemPricingLocal = (item, changedField = null) => {
    return calculateItemPricing(item, {
      changedField,
      appliquerTvaGlobale: formData.appliquer_tva_globale,
      appliquerRetenueForfaitaire: formData.appliquer_retenue_forfaitaire
    });
  };

  // Fonction pour calculer les totaux du document
  // FONCTION REMPLACÉE - Utilise maintenant l'utilitaire unifié calculateDocumentTotals
  const calculateDocumentTotalsLocal = (items) => {
    return calculateDocumentTotals(items, {
      mode: 'advanced',
      appliquerTvaGlobale: formData.appliquer_tva_globale,
      appliquerRetenueForfaitaire: formData.appliquer_retenue_forfaitaire,
      nbJoursImmobilisation: formData.nb_jours_immobilisation || 0,
      montantJourImmobilisation: formData.montant_jour_immobilisation || 0,
      montantPaye: formData.montant_paye || 0
    });
  };

  // Mettre à jour un item et recalculer les totaux
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    
    // Mettre à jour le champ modifié
    if (field === 'tarif_transporteur_ht' || field === 'marge_pourcentage' || field === 'quantity' || field === 'tva_rate' || field === 'unit_price' || field === 'marge_ht') {
      newItems[index] = {
        ...newItems[index],
        [field]: parseFloat(value) || 0
      };
    } else if (field === 'appliquer_tva') {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    }
    
    // Recalculer automatiquement les prix de cet item en précisant quel champ a été modifié
    newItems[index] = calculateItemPricingLocal(newItems[index], field);
    
    // Recalculer les totaux du document
    const totals = calculateDocumentTotalsLocal(newItems);
    
    setFormData({
      ...formData,
      items: newItems,
      ...totals
    });
  };

  // Ajouter un nouvel item
  const addItem = () => {
    const newItem = {
      ref: '',
      description: '',
      quantity: 1,
      unit: 'remorque',
      poids: '-',
      unite_physique: 'remorque',
      unite_payante: 'unité',  // Maintenant inclut les types de camions
      calcul_automatique: true,  // Par défaut: mode automatique
      tarif_transporteur_ht: 0,
      marge_pourcentage: 20,  // Défaut: 20% marge totale sur les courses
      marge_ht: 0,
      marge_pourcentage_calculee: 0,  // Marge calculée en mode manuel
      prix_client_ht: 0,  // Prix client pour mode manuel
      unit_price: 0,
      total_ht: 0,
      tva_rate: 18,
      appliquer_tva: true,
      total_tva: 0,
      total_ttc: 0,
      comment: ''
    };
    
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  // Supprimer un item
  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateDocumentTotalsLocal(newItems);
    
    setFormData({
      ...formData,
      items: newItems,
      ...totals
    });
  };

  // Sélectionner un client depuis la liste
  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setSelectedClient(client);
      setFormData({
        ...formData,
        client: {
          name: client.name || '',
          address: client.address || '',
          rccm: client.rccm || '',
          contact: client.contact_principal || '',
          email: client.email || '',
          phone: client.phone || ''
        }
      });
    }
  };

  // Valider le formulaire - Validation plus permissive pour permettre la création
  const validateForm = () => {
    const newErrors = {};
    
    // Validation client - seul le nom est vraiment requis
    if (!formData.client.name || formData.client.name.trim() === '') {
      newErrors.clientName = 'Le nom du client est requis';
    }
    
    // L'adresse n'est plus obligatoire pour permettre la création rapide
    // if (!formData.client.address) newErrors.clientAddress = 'L\'adresse du client est requise';
    
    // Validations spécifiques aux bons de commande
    if (formData.type === 'bon_commande') {
      if (!bonCommandeInfo.nom_fournisseur) newErrors.transporteurName = 'Le nom du transporteur est requis';
      if (!bonCommandeInfo.telephone_fournisseur) newErrors.transporteurPhone = 'Le téléphone du transporteur est requis';
      if (!bonCommandeInfo.date_execution) newErrors.dateExecution = 'La date d\'exécution est requise';
    }
    
    // Validation items - au moins un item avec description
    if (formData.items.length === 0) {
      newErrors.items = 'Au moins un item est requis';
    } else {
      // Vérifier que le premier item a au moins une description
      const firstItem = formData.items[0];
      if (!firstItem.description || firstItem.description.trim() === '') {
        newErrors.item_0_description = 'La description du premier item est requise';
      }
      if (firstItem.quantity <= 0) {
        newErrors.item_0_quantity = 'La quantité doit être positive';
      }
      if (firstItem.tarif_transporteur_ht < 0) {
        newErrors.item_0_tarif = 'Le tarif transporteur ne peut pas être négatif';
      }
    }
    
    // Validation validité pour devis
    if (formData.type === 'devis') {
      if (!formData.delai_retractation || formData.delai_retractation === '' || formData.delai_retractation === '0') {
        newErrors.validite = 'La validité est requise (nombre de jours)';
      }
    }
    
    // Émetteur et suivi sont automatiquement remplis, pas besoin de validation stricte
    // if (!formData.emetteur) newErrors.emetteur = 'L\'émetteur est requis';
    // if (!formData.suivi_par) newErrors.suivi_par = 'Le responsable du suivi est requis';
    
    // Validation plus simple des items - seulement les erreurs critiques
    formData.items.forEach((item, index) => {
      if (index === 0) return; // Premier item déjà validé ci-dessus
      if (item.description && item.description.trim() !== '') {
        // Si la description est remplie, on valide les autres champs
        if (item.quantity <= 0) newErrors[`item_${index}_quantity`] = 'La quantité doit être positive';
        if (item.tarif_transporteur_ht < 0) newErrors[`item_${index}_tarif`] = 'Le tarif transporteur ne peut pas être négatif';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Afficher les erreurs spécifiques pour aider l'utilisateur
      const errorMessages = Object.values(errors).join('\n');
      alert(`Veuillez corriger les erreurs suivantes dans le formulaire :\n${errorMessages}`);
      console.log('Erreurs de validation:', errors);
      return;
    }
    
    setLoading(true);
    
    try {
      // Recalculer une dernière fois avant soumission
      const calculatedItems = formData.items.map(calculateItemPricingLocal);
      const totals = calculateDocumentTotalsLocal(calculatedItems);
      
      const finalData = {
        ...formData,
        items: calculatedItems,
        ...totals,
        delai_retractation: formData.delai_retractation + ' jours',
        emetteur: formData.emetteur || user?.full_name || 'Baobab Connect',
        suivi_par: formData.suivi_par || user?.full_name || 'Système'
      };
      
      // Ajouter les informations du bon de commande si nécessaire
      if (formData.type === 'bon_commande') {
        finalData.bon_commande_info = {
          ...bonCommandeInfo,
          // Convertir les véhicules pour la compatibilité avec l'ancien format
          tracteur_1: bonCommandeInfo.vehicles[0]?.tracteur || '',
          semi_remorque_1: bonCommandeInfo.vehicles[0]?.semi_remorque || '',
          tracteur_2: bonCommandeInfo.vehicles[1]?.tracteur || '',
          semi_remorque_2: bonCommandeInfo.vehicles[1]?.semi_remorque || '',
          vehicles: bonCommandeInfo.vehicles
        };
        
        // Pour les bons de commande, la date de validité devient la date d'exécution
        if (bonCommandeInfo.date_execution) {
          finalData.date_validite = bonCommandeInfo.date_execution;
        }
      }
      
      await onSubmit(finalData);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur inconnue lors de la création du document';
      alert(`Erreur lors de la création du document: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{
          color: formData.type === 'devis' ? '#16a34a' : 
                 formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
        }}>
          {document ? 'Modifier le document' : 
            formData.type === 'devis' ? 'Créer un nouveau devis' :
            formData.type === 'bon_commande' ? 'Créer un bon de commande' :
            'Créer une nouvelle facture'
          }
        </h2>
        <p className="text-gray-600">
          Remplissez tous les champs obligatoires (*) pour créer votre {
            formData.type === 'devis' ? 'devis' :
            formData.type === 'bon_commande' ? 'bon de commande' :
            'facture'
          }
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Configuration du document - Toujours visible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Configuration du document</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de document *
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleDocumentTypeChange(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="devis">📊 Devis / Cotation</option>
                <option value="bon_commande">📋 Bon de commande</option>
                <option value="facture">🧾 Facture</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Style de template
              </label>
              <select
                value={formData.template_style}
                onChange={(e) => setFormData({ ...formData, template_style: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="standard">Standard</option>
                <option value="advanced">Avancé</option>
              </select>
            </div>
          </div>
        </div>

        {/* Informations émetteur/suivi - Toujours visible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Informations de suivi</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Émis par *
              </label>
              <input
                type="text"
                value={formData.emetteur}
                onChange={(e) => setFormData({ ...formData, emetteur: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nom de l'émetteur"
                required
              />
              {errors.emetteur && <p className="text-red-500 text-sm mt-1">{errors.emetteur}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Suivi par *
              </label>
              <input
                type="text"
                value={formData.suivi_par}
                onChange={(e) => setFormData({ ...formData, suivi_par: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Responsable du suivi"
                required
              />
              {errors.suivi_par && <p className="text-red-500 text-sm mt-1">{errors.suivi_par}</p>}
            </div>
          </div>
          
          {/* Phase 2: Nouveau système de statuts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut du document
              </label>
              <select
                value={formData.document_status || ''}
                onChange={(e) => setFormData({ ...formData, document_status: e.target.value || null })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionner un statut --</option>
                {availableDocumentStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {formData.document_status && (
                <div className="mt-2">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    availableDocumentStatuses.find(s => s.value === formData.document_status)?.color || 'bg-gray-100 text-gray-800'
                  }`}>
                    {availableDocumentStatuses.find(s => s.value === formData.document_status)?.label || formData.document_status}
                  </span>
                </div>
              )}
            </div>
            
            {formData.type !== 'devis' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut de paiement
                </label>
                <select
                  value={formData.payment_status || ''}
                  onChange={(e) => setFormData({ ...formData, payment_status: e.target.value || null })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Sélectionner un statut --</option>
                  {availablePaymentStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                {formData.payment_status && (
                  <div className="mt-2">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      availablePaymentStatuses.find(s => s.value === formData.payment_status)?.color || 'bg-gray-100 text-gray-800'
                    }`}>
                      {availablePaymentStatuses.find(s => s.value === formData.payment_status)?.label || formData.payment_status}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Rendu conditionnel selon le type de document */}
        {renderDocumentForm()}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
            style={{
              backgroundColor: formData.type === 'devis' ? '#16a34a' : 
                              formData.type === 'bon_commande' ? '#ea580c' : '#2563eb',
              focusRingColor: formData.type === 'devis' ? '#16a34a' : 
                             formData.type === 'bon_commande' ? '#ea580c' : '#2563eb'
            }}
          >
            {loading ? 'Création...' : 
              formData.type === 'devis' ? 'Créer le devis' :
              formData.type === 'bon_commande' ? 'Créer le bon de commande' :
              'Créer la facture'
            }
          </button>
        </div>
      </form>
    </div>
  );

};

export default DocumentFormWithPricing;